import { StockfishWorker } from "./stockfish-worker";
import { analyzeGame } from "./game-analyzer";
import { reportAnalysisFailure, uploadAnalysis, type UnanalyzedGame } from "./analysis.api";
import { useAnalysisStore, type AnalysisRunSource } from "./analysis.store";
import type { AnalysisPreset } from "./analysis-presets";
import { markFullEngineDownloaded } from "./analysis-presets";
import { useGamesStore } from "../games/games.store";
import { usePatternsStore } from "../patterns/patterns.store";
import { useAnalysisDataStore } from "./analysis-data.store";
import type { FocusCohort } from "../patterns/patterns.api";
import { recordAnalysisCalibration } from "./analysis-calibration";
import { trackProductEvent } from "@/shared/analytics/ga4";
import { ApiRequestError } from "@/shared/api/client";
import { analysisCapabilities, isAnalysisCancellation, normalizeAnalysisError } from "./analysis-error";

const MAX_GAME_ATTEMPTS = 2;
const MAX_UPLOAD_ATTEMPTS = 3;
const PATTERN_COHORTS: readonly FocusCohort[] = ["bullet", "blitz", "rapid", "daily"];
const GAME_TIMEOUT_MS: Record<AnalysisPreset["id"], number> = {
  quick: 6 * 60_000,
  balanced: 20 * 60_000,
  deep: 75 * 60_000,
};

class AnalysisTimeoutError extends Error {
  constructor(minutes: number) {
    super(`Timed out after ${minutes} minutes. Nikola skipped this game and continued the batch.`);
  }
}

const withGameTimeout = <T>(work: Promise<T>, timeoutMs: number, onTimeout: () => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      onTimeout();
      reject(new AnalysisTimeoutError(Math.round(timeoutMs / 60_000)));
    }, timeoutMs);
    work.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });

const isRetryableUploadError = (error: unknown): boolean =>
  !(error instanceof ApiRequestError) || error.status === 408 || error.status === 429 || error.status >= 500;

const uploadWithRetry = async (
  gameId: string,
  result: Awaited<ReturnType<typeof analyzeGame>>,
  preset: AnalysisPreset,
  cancelled: () => boolean,
): ReturnType<typeof uploadAnalysis> => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    if (cancelled()) throw new DOMException("Analysis was stopped.", "AbortError");
    try {
      return await uploadAnalysis(gameId, result, preset.depth, preset.id);
    } catch (error) {
      lastError = error;
      if (cancelled() || attempt === MAX_UPLOAD_ATTEMPTS || !isRetryableUploadError(error)) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, attempt * 500));
    }
  }
  throw lastError;
};

/**
 * Owns the Stockfish engine instance and the game-by-game analysis loop
 * as a module-level singleton, independent of any React component's
 * mount/unmount lifecycle.
 *
 * Why this exists: AnalysisPage used to own the engine + loop directly
 * (as local variables inside its startAnalysis handler), which meant
 * navigating away from /analyze to /games or /chat unmounted the page
 * and abandoned the in-progress batch — nothing was left driving the
 * loop forward, even though the underlying Web Worker would have kept
 * running fine in the background. Moving ownership here means the loop
 * survives route changes as long as the app itself (this JS context)
 * stays alive — it does NOT survive a tab close/reload, since a Web
 * Worker's page context ends with the page, and Stockfish moved
 * client-side specifically so there's no server-side job to fall back
 * to (see git history around the WASM migration).
 *
 * AnalysisPage becomes a thin view over useAnalysisStore's state and
 * calls into this singleton's start()/stop() rather than running the
 * loop itself.
 */
class AnalysisRunner {
  private engine: StockfishWorker | null = null;
  private revision = 0;
  private isActive = false;

  get running(): boolean {
    return this.isActive;
  }

  async start(
    unanalyzed: UnanalyzedGame[],
    preset: AnalysisPreset,
    source: AnalysisRunSource = "batch",
  ): Promise<boolean> {
    const completion = this.tryStart(unanalyzed, preset, source);
    if (!completion) return false;
    await completion;
    return true;
  }

  tryStart(
    unanalyzed: UnanalyzedGame[],
    preset: AnalysisPreset,
    source: AnalysisRunSource = "batch",
  ): Promise<void> | null {
    if (this.isActive) return null;
    if (useGamesStore.getState().importProgress != null) return null;
    if (unanalyzed.length === 0) return null;
    return this.run(unanalyzed, preset, source);
  }

  private async run(unanalyzed: UnanalyzedGame[], preset: AnalysisPreset, source: AnalysisRunSource): Promise<void> {
    const store = useAnalysisStore.getState();
    const revision = ++this.revision;
    const cancelled = () => revision !== this.revision;
    this.isActive = true;

    // Single-game analysis owns its preset locally, unlike AnalysisPage.
    // Publish the actual run preset before exposing download progress so every
    // status surface describes the engine that is really being initialized.
    store.setPreset(preset);
    store.reset(source);
    store.addJobs(unanalyzed.map((g) => ({ gameId: g.id, opponent: g.opponent, preset })));
    store.setRunning(true);

    let engine = new StockfishWorker();
    this.engine = engine;

    try {
      store.setInitError(null);
      store.setEnginePhase("Preparing Nikola's analysis...");
      await engine.init(preset, (percent) => {
        if (!cancelled()) store.setDownloadPercent(percent);
      });
      if (cancelled()) {
        engine.destroy();
        return;
      }
      if (preset.useFullEngine) {
        markFullEngineDownloaded();
        store.setFullEngineDownloaded(true);
      }
      store.setDownloadPercent(null);
      store.setEnginePhase(null);
    } catch (err) {
      engine.destroy();
      if (cancelled()) return;
      if (isAnalysisCancellation(err)) {
        this.isActive = false;
        this.engine = null;
        return;
      }
      const error = normalizeAnalysisError(err, "engine_init").userMessage;
      store.setInitError(error);
      for (const game of unanalyzed) {
        store.updateJob(game.id, { status: "error", error });
        store.incrementCompleted();
      }
      store.setRunning(false);
      store.setDownloadPercent(null);
      store.setEnginePhase(null);
      this.isActive = false;
      this.engine = null;
      return;
    }

    const recoverEngine = async () => {
      engine.destroy();
      engine = new StockfishWorker();
      this.engine = engine;
      store.setEnginePhase("Recovering Nikola's engine...");
      await engine.init(preset, (percent) => {
        if (!cancelled()) store.setDownloadPercent(percent);
      });
      if (cancelled()) return;
      store.setDownloadPercent(null);
      store.setEnginePhase(null);
    };

    const changedPatternCohorts = new Set<FocusCohort>();
    for (const game of unanalyzed) {
      if (cancelled()) break;
      const startedAt = Date.now();
      let completed = false;
      let attemptsUsed = 0;
      let finalFailure = normalizeAnalysisError(undefined, "analysis");
      for (let attempt = 1; attempt <= MAX_GAME_ATTEMPTS && !cancelled(); attempt++) {
        attemptsUsed = attempt;
        store.updateJob(game.id, { status: "analyzing", startedAt, attempts: attempt, error: null });
        try {
          const result = await withGameTimeout(
            analyzeGame(engine, game.pgn, game.playerColor, game.id, preset, (progress) => {
              if (!cancelled()) store.updateJob(game.id, { progress });
            }),
            GAME_TIMEOUT_MS[preset.id],
            () => engine.destroy(),
          );
          if (cancelled()) break;
          let upload: Awaited<ReturnType<typeof uploadAnalysis>>;
          try {
            upload = await uploadWithRetry(game.id, result, preset, cancelled);
          } catch (uploadError) {
            finalFailure = normalizeAnalysisError(uploadError, "upload");
            break;
          }
          if (cancelled()) break;
          store.updateJob(game.id, {
            status: "done",
            result: { ...result, accuracy: game.chessComAccuracy ?? result.accuracy },
            resultLabel: upload.saved ? "saved" : "kept-stronger",
            durationMs: Date.now() - startedAt,
          });
          store.incrementCompleted();
          trackProductEvent("analysis_completed");
          void recordAnalysisCalibration(
            preset,
            result.moves.filter((move) => move.isPlayerMove).length,
            Date.now() - startedAt,
          );
          completed = true;
          if (PATTERN_COHORTS.includes(game.timeControl as FocusCohort))
            changedPatternCohorts.add(game.timeControl as FocusCohort);
          break;
        } catch (err) {
          if (cancelled() || isAnalysisCancellation(err)) break;
          finalFailure = normalizeAnalysisError(err, "analysis");
          if (attempt < MAX_GAME_ATTEMPTS && !cancelled()) {
            store.updateJob(game.id, { status: "queued", error: `Attempt ${attempt} failed. Retrying once…` });
            try {
              await recoverEngine();
            } catch (recoveryError) {
              finalFailure = normalizeAnalysisError(recoveryError, "engine_init");
              break;
            }
          }
        }
      }
      if (!completed && !cancelled()) {
        const error = finalFailure.userMessage;
        store.updateJob(game.id, { status: "error", error, durationMs: Date.now() - startedAt });
        void reportAnalysisFailure(game.id, {
          attempts: attemptsUsed,
          error: finalFailure.diagnostic,
          phase: finalFailure.phase,
          preset: preset.id,
          capabilities: analysisCapabilities(),
        }).catch(() => undefined);
        store.incrementCompleted();
      }
    }

    engine.destroy();
    if (cancelled()) return;
    this.engine = null;
    this.isActive = false;
    store.setRunning(false);
    // Keep route-level Zustand caches current after a background batch;
    // navigating to Games or Patterns must not require a manual reload.
    void Promise.all([
      useGamesStore.getState().refreshGames(),
      useAnalysisDataStore.getState().refreshUnanalyzed(),
      ...[...changedPatternCohorts].map((cohort) => usePatternsStore.getState().refreshPatterns(cohort)),
    ]).catch(() => undefined);
  }

  stop(): void {
    this.revision += 1;
    this.engine?.stop();
    this.engine?.destroy();
    this.engine = null;
    this.isActive = false;
    const store = useAnalysisStore.getState();
    store.clearSession();
    store.setRunning(false);
  }
}

/** Single shared instance for the whole app — imported by AnalysisPage instead of instantiating its own runner. */
export const analysisRunner = new AnalysisRunner();
